/**
 * Who can see a saved report: the one set of words and the one choice every
 * save box uses (the items list's "Save query", the Report Designer's save box,
 * the Saved Reports panel's). Roadmap N-8, migration 1023.
 *
 * The rule on the server, which the words here state:
 *   - Everyone in the workspace: any member can open it, change it or delete it.
 *   - Just me: only the person who saved it can.
 *   - Only the person who saved it can move it between the two, and a report a
 *     Smart Collection or an item pool reads stays with the workspace.
 *
 * Rows saved before migration 1023 have no recorded owner, so they stay shared
 * and nobody is offered the choice on them.
 */

import { useId } from 'react';
import { ChoiceGroup, Radio } from '../../ds';
import { describeReportDependents, getReportDependents } from '../../../lib/reportDependents';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { ReportVisibility, SavedReport } from '../../../types/prioritization/savedReports';

const EVERYONE_CAN = 'Everyone in the workspace can open and change this report.';

/** Why this person gets a line instead of the choice, as that line; null when they may choose. */
function audienceFixedBy(report: SavedReport | undefined, userId: string): string | null {
  if (!report) return null;
  if (!report.createdBy) return EVERYONE_CAN;
  if (report.createdBy !== userId) {
    return `${EVERYONE_CAN} Only the person who saved it can change who can see it.`;
  }
  if (report.visibility === 'private') return null;
  const readers = describeReportDependents(getReportDependents(report.id));
  return readers ? `${EVERYONE_CAN} ${readers}` : null;
}

export interface ReportAudienceFieldProps {
  /** The report being updated. Unset when saving a new one. */
  report?: SavedReport;
  value: ReportVisibility;
  onChange: (next: ReportVisibility) => void;
}

/**
 * The choice, when this person may make it; otherwise a plain line saying who
 * can see the report and what would have to change.
 */
export function ReportAudienceField({ report, value, onChange }: ReportAudienceFieldProps) {
  const userId = useUserStore((s) => s.userId);
  const name = useId();
  const fixed = audienceFixedBy(report, userId);

  if (fixed) {
    return <p className="text-xs text-[var(--text-ter)]" data-testid="report-audience-line">{fixed}</p>;
  }

  return (
    <ChoiceGroup legend="Who can see this">
      <Radio
        name={name}
        checked={value === 'workspace'}
        onChange={() => onChange('workspace')}
        label="Everyone in the workspace"
        description="Anyone here can open and change it."
      />
      <Radio
        name={name}
        checked={value === 'private'}
        onChange={() => onChange('private')}
        label="Just me"
        description="Only you can open or change it."
      />
    </ChoiceGroup>
  );
}

/** The mark on a list row: the one thing in a list that differs from the default. */
export function JustMeTag() {
  return (
    <span
      className="shrink-0 border border-[var(--rule)] px-1 font-mono text-[10px] uppercase tracking-wide text-[var(--text-ter)]"
      title="Only you can see this report."
    >
      Just me
    </span>
  );
}
